import React, { createContext, useContext, ReactNode } from 'react';
import { runtimeEnvironment } from '../config/environment';

const ENV_BG: Partial<Record<string, string>> = {
    'dev-local': '#dbeafe',
    'test': '#fef9c3',
    'cert': '#dcfce7',
};

// Theme interface
export interface Theme {
    colors: {
        primary: string;
        primaryHover: string;
        secondary: string;
        tertiary: string;
        quaternary: string;
        background: string;
        surface: string;
        surfaceSecondary: string;
        text: {
            primary: string;
            secondary: string;
            muted: string;
            inverse: string;
        };
        border: {
            default: string;
            focus: string;
            error: string;
        };
        status: {
            success: string;
            warning: string;
            error: string;
            info: string;
            disabled: string;
        };
    };
    spacing: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        xxl: string;
    };
    borderRadius: {
        sm: string;
        md: string;
        lg: string;
        xl: string;
    };
    typography: {
        fontFamily: string;
        fontSize: {
            xs: string;
            sm: string;
            md: string;
            lg: string;
            xl: string;
            xxl: string;
        };
        fontWeight: {
            normal: number;
            medium: number;
            semibold: number;
            bold: number;
        };
        lineHeight: {
            tight: number;
            normal: number;
            relaxed: number;
        };
    };
    shadows: {
        sm: string;
        md: string;
        lg: string;
    };
    transitions: {
        fast: string;
        normal: string;
        slow: string;
    };
}

// Default theme
const defaultTheme: Theme = {
    colors: {
        primary: '#3f2a56',        // Logo dark purple
        primaryHover: '#2d1f3d',   // Darker shade of primary
        secondary: '#6558b1',      // Logo medium purple
        tertiary: '#7ba6de',       // Logo light blue
        quaternary: '#b9d9eb',     // Logo very light blue
        background: ENV_BG[runtimeEnvironment] ?? '#e2e8f0',
        surface: '#ffffff',
        surfaceSecondary: '#f1f5f9',
        text: {
            primary: '#1f2937',
            secondary: '#374151',
            muted: '#6b7280',
            inverse: '#ffffff',
        },
        border: {
            default: '#e2e8f0',
            focus: '#6558b1',      // Use logo medium purple for focus
            error: '#dc2626',
        },
        status: {
            success: '#10b981',
            warning: '#f59e0b',
            error: '#dc2626',
            info: '#7ba6de',       // Use logo light blue for info
            disabled: '#9ca3af',
        },
    },
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        xxl: '48px',
    },
    borderRadius: {
        sm: '4px',
        md: '6px',
        lg: '8px',
        xl: '12px',
    },
    typography: {
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        fontSize: {
            xs: '12px',
            sm: '14px',
            md: '16px',
            lg: '18px',
            xl: '20px',
            xxl: '28px',
        },
        fontWeight: {
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
        },
        lineHeight: {
            tight: 1.25,
            normal: 1.5,
            relaxed: 1.75,
        },
    },
    shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    transitions: {
        fast: '0.15s ease',
        normal: '0.2s ease',
        slow: '0.3s ease',
    },
};

// Theme context
const ThemeContext = createContext<Theme>(defaultTheme);

// Theme provider props
interface ThemeProviderProps {
    children: ReactNode;
    theme?: Partial<Theme>;
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    theme: customTheme
}) => {
    const theme = customTheme ? { ...defaultTheme, ...customTheme } : defaultTheme;

    return (
        <ThemeContext.Provider value={theme}>
            {children}
        </ThemeContext.Provider>
    );
};

// Hook to use theme
export const useTheme = (): Theme => {
    const theme = useContext(ThemeContext);
    if (!theme) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return theme;
};

// Utility function to create themed styles
export const createThemedStyles = (theme: Theme) => ({
    // Button styles
    button: {
        primary: {
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.inverse,
            border: 'none',
            borderRadius: theme.borderRadius.lg,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: `background-color ${theme.transitions.normal}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing.sm,
        },
        secondary: {
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.inverse,
            border: 'none',
            borderRadius: theme.borderRadius.lg,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: 'pointer',
            transition: `background-color ${theme.transitions.normal}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing.sm,
        },
        disabled: {
            backgroundColor: theme.colors.status.disabled,
            cursor: 'not-allowed',
            opacity: 0.6,
        },
    },

    // Input styles
    input: {
        default: {
            width: '100%',
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.lg,
            fontSize: theme.typography.fontSize.md,
            transition: `border-color ${theme.transitions.normal}`,
            backgroundColor: theme.colors.surface,
            outline: 'none',
        },
        focus: {
            borderColor: theme.colors.border.focus,
            boxShadow: `0 0 0 3px ${theme.colors.border.focus}20`,
        },
        error: {
            borderColor: theme.colors.border.error,
            boxShadow: `0 0 0 3px ${theme.colors.border.error}20`,
        },
    },

    // Card styles
    card: {
        default: {
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.xl,
            boxShadow: theme.shadows.md,
            padding: theme.spacing.xl,
        },
        secondary: {
            backgroundColor: theme.colors.surfaceSecondary,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border.default}`,
            padding: theme.spacing.lg,
        },
    },

    // Typography styles
    text: {
        heading: {
            fontSize: theme.typography.fontSize.xxl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text.primary,
            margin: 0,
            lineHeight: theme.typography.lineHeight.tight,
        },
        subheading: {
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.semibold,
            color: theme.colors.text.primary,
            margin: 0,
            lineHeight: theme.typography.lineHeight.normal,
        },
        body: {
            fontSize: theme.typography.fontSize.md,
            fontWeight: theme.typography.fontWeight.normal,
            color: theme.colors.text.secondary,
            lineHeight: theme.typography.lineHeight.normal,
        },
        muted: {
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.normal,
            color: theme.colors.text.muted,
            lineHeight: theme.typography.lineHeight.normal,
        },
        label: {
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.secondary,
        },
    },

    // Layout styles
    container: {
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        padding: theme.spacing.lg,
    },

    // Alert styles
    alert: {
        error: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing.sm,
            backgroundColor: '#fef2f2',
            border: `1px solid #fecaca`,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md,
            marginBottom: theme.spacing.lg,
        },
        success: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing.sm,
            backgroundColor: '#f0fdf4',
            border: `1px solid #bbf7d0`,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md,
            marginBottom: theme.spacing.lg,
        },
    },
});

export default ThemeContext;
